import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/api/axios';
import KpiSection from './KpiSection';
import ChartsSection from './ChartsSection';
import { Skeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { format } from 'date-fns';

const { FiAlertCircle, FiRefreshCw, FiLock, FiCalendar } = FiIcons;

const Dashboard = () => {
  const REFRESH_INTERVAL = 3600000; // 60 minutes

  const { data: bundle, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['dashboard_bundle'],
    queryFn: async () => {
      const response = await api.get('dashboard_bundle.php');
      return response.data;
    },
    refetchInterval: REFRESH_INTERVAL,
    retry: 1
  });

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}
        </div>
        <div className="space-y-6">
          <Skeleton className="h-[350px] rounded-3xl w-full" />
          <Skeleton className="h-[350px] rounded-3xl w-full" />
        </div>
      </div>
    );
  }

  if (isError || !bundle?.status) {
    const isAuthError = error?.response?.status === 401;
    return (
      <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[2rem] border-2 border-dashed border-surface-200 px-6 text-center shadow-sm">
        <div className={`w-20 h-20 ${isAuthError ? 'bg-amber-50' : 'bg-red-50'} rounded-3xl flex items-center justify-center mb-6`}>
          <SafeIcon icon={isAuthError ? FiLock : FiAlertCircle} className={`w-10 h-10 ${isAuthError ? 'text-amber-500' : 'text-red-500'}`} />
        </div>
        <h2 className="text-2xl font-extrabold text-surface-900 mb-2">Connection Problem</h2>
        <p className="text-surface-500 max-w-md mb-8 leading-relaxed">
          {isAuthError 
            ? "Your authentication token has expired. Please sign in again to continue."
            : error?.message || "We encountered an error while fetching the dashboard statistics."}
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button onClick={() => refetch()} className="px-8 h-12 rounded-2xl min-w-[160px]">
            <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            Sync Now
          </Button>
        </div>
      </div>
    );
  }

  const dashboardData = bundle.data;

  return (
    <div className="space-y-8 pb-10">
      {/* Dashboard Actions Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center space-x-3 text-surface-500">
          <div className="p-2.5 bg-white rounded-xl border border-surface-200 shadow-sm">
            <SafeIcon icon={FiCalendar} className="w-4 h-4 text-primary-600" />
          </div>
          <div>
            <p className="text-[10px] uppercase font-bold tracking-widest text-surface-400">Campaign Insights</p>
            <span className="text-sm font-bold text-surface-700">{format(new Date(), 'eeee, do MMMM')}</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="hidden sm:block text-right">
            <p className="text-[10px] font-bold text-surface-400 uppercase tracking-tighter">Auto-sync: 60m</p>
            <p className="text-[10px] font-medium text-surface-300">Last: {format(new Date(), 'HH:mm:ss')}</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => refetch()}
            className="rounded-xl border-surface-200 hover:border-primary-300 bg-white h-10 px-4"
            disabled={isFetching}
          >
            <SafeIcon icon={FiRefreshCw} className={`w-3.5 h-3.5 mr-2 text-primary-600 ${isFetching ? 'animate-spin' : ''}`} />
            <span className="text-xs font-bold uppercase tracking-wider">Manual Refresh</span>
          </Button>
        </div>
      </div>

      <KpiSection data={dashboardData.kpi} />
      
      <ChartsSection 
        giftDist={dashboardData.gift_distribution} 
        dailyTrend={dashboardData.daily_trend}
        cityStats={dashboardData.city_stats}
        funnel={dashboardData.funnel}
        liveActivity={dashboardData.live_activity}
      />
    </div>
  );
};

export default Dashboard;